import { useEffect, useRef } from "react";

// Warsaw coordinates
const LAT = 52.2297;
const LNG = 21.0122;

// NOAA solar calculation. Returns sunrise & sunset as Date objects for the given local day.
function calcSun(date: Date, lat: number, lng: number) {
  const rad = Math.PI / 180;
  const deg = 180 / Math.PI;

  // Day of year
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  const dayOfYear = Math.floor(diff / 86400000);

  const calcForZenith = (zenith: number, rising: boolean) => {
    const lngHour = lng / 15;
    const t = dayOfYear + ((rising ? 6 : 18) - lngHour) / 24;
    const M = 0.9856 * t - 3.289;
    let L =
      M +
      1.916 * Math.sin(M * rad) +
      0.02 * Math.sin(2 * M * rad) +
      282.634;
    L = (L + 360) % 360;
    let RA = deg * Math.atan(0.91764 * Math.tan(L * rad));
    RA = (RA + 360) % 360;
    const Lquadrant = Math.floor(L / 90) * 90;
    const RAquadrant = Math.floor(RA / 90) * 90;
    RA = RA + (Lquadrant - RAquadrant);
    RA = RA / 15;
    const sinDec = 0.39782 * Math.sin(L * rad);
    const cosDec = Math.cos(Math.asin(sinDec));
    const cosH =
      (Math.cos(zenith * rad) - sinDec * Math.sin(lat * rad)) /
      (cosDec * Math.cos(lat * rad));
    if (cosH > 1 || cosH < -1) return null; // sun never rises/sets
    let H = rising ? 360 - deg * Math.acos(cosH) : deg * Math.acos(cosH);
    H = H / 15;
    const T = H + RA - 0.06571 * t - 6.622;
    let UT = T - lngHour;
    UT = ((UT % 24) + 24) % 24;
    const result = new Date(date);
    result.setUTCHours(Math.floor(UT), Math.floor((UT % 1) * 60), Math.floor((((UT % 1) * 60) % 1) * 60), 0);
    return result;
  };

  return {
    sunrise: calcForZenith(90.833, true),
    sunset: calcForZenith(90.833, false),
  };
}

function applyTheme(isDark: boolean) {
  const root = document.documentElement;
  if (isDark) root.classList.add("dark");
  else root.classList.remove("dark");
}

function computeAndApply(): Date {
  const now = new Date();
  const today = calcSun(now, LAT, LNG);
  const tomorrow = calcSun(new Date(now.getTime() + 86400000), LAT, LNG);

  let isDark: boolean;
  let nextChange: Date | null = null;

  if (today.sunrise && today.sunset) {
    if (now < today.sunrise) {
      isDark = true;
      nextChange = today.sunrise;
    } else if (now < today.sunset) {
      isDark = false;
      nextChange = today.sunset;
    } else {
      isDark = true;
      nextChange = tomorrow.sunrise ?? new Date(now.getTime() + 3600_000);
    }
  } else {
    const hour = now.getHours();
    isDark = hour < 6 || hour >= 18;
    nextChange = new Date(now.getTime() + 3600_000);
  }

  applyTheme(isDark);
  return nextChange;
}

export function useSunTheme() {
  const timeoutRef = useRef<number | null>(null);
  useEffect(() => {
    const tick = () => {
      const next = computeAndApply();
      const delay = Math.min(
        Math.max(1000, next.getTime() - Date.now() + 500),
        24 * 3600 * 1000,
      );
      timeoutRef.current = window.setTimeout(tick, delay);
    };
    tick();
    // safety net for tab sleep / clock drift
    const intervalId = window.setInterval(() => {
      computeAndApply();
    }, 5 * 60 * 1000);
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      window.clearInterval(intervalId);
    };
  }, []);
}